import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  structuredData?: object;
}

const DEFAULT_TITLE = "SiteBook - Campsite Booking & Management Platform";
const DEFAULT_DESCRIPTION = "Professional campsite booking system with secure payments, automated communications, facility management, and comprehensive reporting. Streamline your outdoor venue operations.";
const DEFAULT_IMAGE = "https://sitebook.uk/images/og-image.png";
const BASE_URL = "https://sitebook.uk";

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = "campsite booking, campsite management, outdoor venue software, booking system, camping reservations, glamping software, holiday park management",
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  noindex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | SiteBook` : DEFAULT_TITLE;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SiteBook",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": DEFAULT_DESCRIPTION,
    "url": BASE_URL,
    "offers": {
      "@type": "Offer",
      "price": "49",
      "priceCurrency": "GBP",
      "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    },
    "provider": {
      "@type": "Organization",
      "name": "Trakto Software Ltd",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "20-22 Wenlock Road",
        "addressLocality": "London",
        "postalCode": "N1 7GU",
        "addressCountry": "GB"
      }
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="SiteBook" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@SiteBookUK" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
}
